import * as React from "react";
import { useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
  Box,
  Link,
  Paper,
  Step,
  StepButton,
  Stepper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
} from "@mui/material";
import Input from "../components/Input";
import Row from "../components/Row";
import { colors, USER_DATA } from "../utils/constants";
import ButtonLoading from "../components/ButtonLoading";
import { formatDatePtBr } from "../utils/dateUtils";
import moment from "moment-timezone";

import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage } from "../api/firebase";
import { toast } from "react-toastify";
import { createEvent, updateEvent } from "../api/events";
import Loading from "../components/Loading";
import canva from "../assets/canva.png";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DialogConfirm from "../components/DialogConfirm";

interface DialogCreateEventFormProps {
  close: () => void;
  open: boolean;
  event?: any;
}
export default function DialogCreateEventForm({
  close,
  open,
  event,
}: DialogCreateEventFormProps) {
  const [eventId, setEventId] = useState<string>("");
  const [banner, setBanner] = useState<string>("");
  const [certificate, setCertificate] = useState<string>("");

  const [colorCertificateConfig, setColorCertificateConfig] =
    useState<string>("");
  const [percentPresenceConfig, setPercentPresenceConfig] =
    useState<number>(100);

  const [title, setTitle] = useState<string>("");
  const [subtitle, setSubtitle] = useState<string>("");

  const [dateInit, setDateInit] = useState<string>("");
  const [dateFinal, setDateFinal] = useState<string>("");

  const [days, setDays] = useState<any>([]);
  const [dayTitle, setDayTitle] = useState<string>("");
  const [dayDate, setDayDate] = useState<string>("");
  const [dayCode, setDayCode] = useState<string>("");

  const [active, setActive] = useState(false);

  const [loading, setLoading] = useState(false);

  const bannerInputRef = useRef<HTMLInputElement>(null);
  const certificateInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (event) {
      setEventId(event.id);
      setBanner(event.banner);
      setCertificate(event.certificate);
      setColorCertificateConfig(event.colorCertificateConfig);
      setTitle(event.title);
      setSubtitle(event.subtitle);
      setDateInit(moment(event?.dateInit).format("YYYY-MM-DD"));
      setDateFinal(moment(event?.dateFinal).format("YYYY-MM-DD"));
      setDays(event.days);
      setPercentPresenceConfig(event.percentPresenceConfig);
      setActive(event.active);
    } else {
      setEventId("");
      setBanner("");
      setCertificate("");
      setColorCertificateConfig("#fff");
      setPercentPresenceConfig(100);
      setTitle("");
      setSubtitle("");
      setDateInit("");
      setDateFinal("");
      setDays([]);
      setActiveStep(0);
    }
  }, [event]);

  const uploadImage = (inp: any, setInput: any) => {
    if (!inp) return;

    setLoading(true);
    const imageRef = ref(storage, `images/${new Date().getTime()}`);

    uploadBytes(imageRef, inp).then((result) => {
      getDownloadURL(result.ref).then((imageUpload) => {
        setInput(imageUpload);
        setLoading(false);
        return imageUpload;
      });
    });
  };

  const addDay = () => {
    if (!dayTitle || !dayDate || !dayCode) {
      toast.error("Preencha todos os campos para os dias do evento!");
      return;
    }

    const dateFormated = moment(dayDate)
      .tz("America/Sao_Paulo")
      .startOf("day")
      .toISOString();

    const newDay = {
      title: dayTitle,
      date: dateFormated,
      code: dayCode,
      id: new Date().getTime().toString(),
    };

    setDays([...days, newDay]);

    setDayTitle("");
    setDayDate("");
    setDayCode("");
  };

  const saveEvent = () => {
    const dateInitFormated = moment(dateInit)
      .tz("America/Sao_Paulo")
      .startOf("day")
      .toISOString();
    const dateFinalFormated = moment(dateFinal)
      .tz("America/Sao_Paulo")
      .startOf("day")
      .toISOString();

    const client_id = JSON.parse(
      localStorage.getItem(USER_DATA) || "{}"
    ).client_id;

    if (eventId) {
      const newEvent = {
        title,
        subtitle,
        banner,
        certificate,
        dateInit: dateInitFormated,
        dateFinal: dateFinalFormated,
        days,
        client_id: client_id ? client_id : "",
        active,
        colorCertificateConfig,
        percentPresenceConfig: Number(percentPresenceConfig),
      };

      updateEvent(eventId, newEvent)
        .then(() => {
          toast.success("Evento atualizado com sucesso!");
          close();
        })
        .catch(() => {
          toast.error("Erro ao atualizar evento! Tente novamente");
        });
    } else {
      const newEvent = {
        id: new Date().getTime().toString(),
        title,
        subtitle,
        banner,
        certificate,
        dateInit: dateInitFormated,
        dateFinal: dateFinalFormated,
        days,
        client_id: client_id ? client_id : "",
        active,
        colorCertificateConfig,
        percentPresenceConfig: Number(percentPresenceConfig),
      };
      createEvent(newEvent)
        .then(() => {
          toast.success("Evento criado com sucesso!");
          close();
        })
        .catch((err) => {
          toast.error("Erro ao criar evento! Tente novamente");
        });
    }
  };

  const goToNextStep = () => {
    if (activeStep == 1) {
      if (!certificate) {
        toast.error("Preencha todos os campos!");
        return;
      }
    }

    if (activeStep == 2) {
      if (!days) {
        toast.error("Preencha todos os campos!");
        return;
      }
    }

    setActiveStep(activeStep + 1);
  };

  const steps = ["Informações básicas", "Certificado", "Dias do evento"];

  const [activeStep, setActiveStep] = useState(0);

  const [editingDay, setEditingDay] = useState<{
    id: string;
    title: string;
    date: string;
    code: string;
  } | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [newDate, setNewDate] = useState("");
  const [newCode, setNewCode] = useState("");

  const handleEditClick = (day: any) => {
    setEditingDay(day);
    setNewTitle(day.title);
    setNewDate(day.date);
    setNewCode(day.code);
  };

  const handleUpdate = () => {
    const updatedDays = days.map((day: any) => {
      if (day.id === editingDay?.id) {
        return {
          ...day,
          title: newTitle,
          date: newDate,
          code: newCode,
        };
      }
      return day;
    });

    setDays(updatedDays); // Atualiza o estado do array days
    setEditingDay(null); // Limpa o estado de edição
  };

  const [dayToDelete, setDayToDelete] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [openConfirmDeleteDay, setOpenConfirmDeleteDay] = useState(false);

  const handleDeleteClick = (day: any) => {
    setDayToDelete(day);
    setOpenConfirmDeleteDay(true);
  };

  const handleConfirmDeleteDay = (response: boolean) => {
    if (response && dayToDelete) {
      setDays(days.filter((day: any) => day.id !== dayToDelete.id));
    }
    setOpenConfirmDeleteDay(false);
    setDayToDelete(null);
  };

  const isFutureOrToday = (date: string) => {
    const inputDate = moment(date, "YYYY-MM-DD");
    const today = moment().startOf("day");
    return inputDate.isSameOrAfter(today);
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      PaperProps={{ sx: styles.dialogPaper }}
    >
      <DialogTitle sx={styles.dialogTitle}>
        {event?.id ? "Editar evento" : "Novo evento"}
      </DialogTitle>

      <Box style={styles.stepperWrap}>
        <Stepper activeStep={activeStep} nonLinear alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label} completed={index < activeStep}>
              <StepButton color="inherit" onClick={() => setActiveStep(index)}>
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={styles.dialogContent}>
        {activeStep === 0 && (
          <>
            <Typography sx={styles.title}>Dados do evento</Typography>

            <Input
              title="Titulo do evento"
              placeholder="Digite o titulo do evento"
              value={title}
              style={{ width: "100%" }}
              onChange={(e) => setTitle(e.target.value)}
            />

            <Input
              title="Descição"
              placeholder="Digite a descrição do evento"
              value={subtitle}
              multiline
              rows={4}
              style={{ width: "100%" }}
              onChange={(e) => setSubtitle(e.target.value)}
            />

            <Row style={{ alignItems: "center", gap: 12 }}>
              <Input
                type="date"
                title="Data inicio"
                placeholder="Digite a data do dia"
                value={dateInit}
                onChange={(e) => {
                  setDateInit(e.target.value);
                }}
              />
              <Input
                type="date"
                title="Data final"
                placeholder="Digite a data do dia"
                value={dateFinal}
                onChange={(e) => setDateFinal(e.target.value)}
              />
            </Row>

            <Row style={styles.activeRow}>
              <Switch
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
              <span style={styles.activeLabel}>Evento ativo</span>
            </Row>

            <Box style={styles.sectionDivider} />

            <Typography sx={styles.title}>Banner do evento</Typography>
            <span style={styles.hintText}>
              Tamanho recomendado: <strong>760 × 425px</strong>
            </span>

            <input
              ref={bannerInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadImage(file, setBanner);
              }}
            />

            {loading ? (
              <Loading text="Enviando..." color={colors.primary} />
            ) : (
              <Box
                style={styles.uploadCard}
                onClick={() => bannerInputRef.current?.click()}
              >
                {banner ? (
                  <>
                    <img
                      src={banner}
                      style={styles.uploadPreviewImg as React.CSSProperties}
                      alt="Banner do evento"
                    />
                    <Box style={styles.uploadOverlay}>
                      <CloudUploadOutlinedIcon sx={{ fontSize: 18 }} />
                      Trocar banner
                    </Box>
                  </>
                ) : (
                  <Box style={styles.uploadEmptyState}>
                    <ImageOutlinedIcon sx={{ fontSize: 40, color: "#B8C2D3" }} />
                    <span style={styles.uploadEmptyTitle}>
                      Clique para enviar o banner
                    </span>
                    <span style={styles.uploadEmptyHint}>
                      PNG ou JPG, 760 × 425px
                    </span>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}

        {activeStep === 1 && (
          <>
            <Typography sx={styles.title}>Certificado</Typography>

            <Row style={styles.canvaRow}>
              <img src={canva} height={28} width={28} alt="Canva" />
              <Link
                href={
                  "https://www.canva.com/design/DAGcdj8y_iA/8oqtvxZLtMxUuPySVJSlqg/view?utm_content=DAGcdj8y_iA&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview"
                }
                target="_blank"
                style={styles.canvaLink}
              >
                Baixar modelo de certificado
              </Link>
            </Row>

            <Box style={styles.warningBox}>
              <span style={styles.warningText}>
                O modelo pode ser alterado conforme sua necessidade, mas{" "}
                <strong>
                  NÃO ALTERAR O LOCAL DA LINHA DO NOME DO PARTICIPANTE
                </strong>
                . Qualquer dúvida pode contatar o suporte.
              </span>
            </Box>

            <Row style={styles.certificateStepRow}>
              <Box style={styles.certificateFormCol}>
                <input
                  ref={certificateInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadImage(file, setCertificate);
                  }}
                />

                {loading ? (
                  <Loading text="Enviando..." color={colors.primary} />
                ) : (
                  <Box
                    style={styles.uploadCardSmall}
                    onClick={() => certificateInputRef.current?.click()}
                  >
                    <Box style={styles.uploadEmptyState}>
                      <ImageOutlinedIcon
                        sx={{ fontSize: 32, color: "#B8C2D3" }}
                      />
                      <span style={styles.uploadEmptyTitle}>
                        {certificate
                          ? "Clique para trocar o certificado"
                          : "Clique para enviar o certificado"}
                      </span>
                    </Box>
                  </Box>
                )}

                {certificate && !loading && (
                  <>
                    <Box style={styles.colorFieldWrap}>
                      <Input
                        title="Cor do nome no certificado"
                        placeholder="Ex.: #ffffff"
                        value={colorCertificateConfig}
                        onChange={(e) =>
                          setColorCertificateConfig(e.target.value)
                        }
                      />
                      <Box
                        style={{
                          ...styles.colorSwatch,
                          backgroundColor: colorCertificateConfig || "#fff",
                        }}
                      />
                    </Box>
                    <Input
                      title="Porcentagem de presença obriagatória"
                      placeholder="Digite a porcentagem de presença obriagatória"
                      value={percentPresenceConfig.toString()}
                      onChange={(e) =>
                        setPercentPresenceConfig(e.target.value as any)
                      }
                    />
                  </>
                )}
              </Box>

              <Box style={styles.certificatePreviewWrap}>
                <Box
                  style={{
                    ...(styles.certificate as React.CSSProperties),
                    backgroundImage: `url(${certificate})`,
                  }}
                />
              </Box>
            </Row>
          </>
        )}

        {activeStep === 2 && (
          <Box sx={{ width: "100%" }}>
            <Typography sx={styles.title}>Dias</Typography>

            <Row
              style={{
                alignItems: "center",
                gap: 12,
                width: "100%",
                display: "flex",
                flexWrap: "wrap",
              }}
            >
              <Input
                title="Titulo do dia"
                placeholder="Digite o titulo do dia"
                value={dayTitle}
                onChange={(e) => setDayTitle(e.target.value)}
                required
                style={{ width: 400 }}
              />
              <Input
                type="date"
                title="Data do dia"
                value={dayDate}
                required
                onChange={(e) => setDayDate(e.target.value)}
                style={{ width: 250 }}
              />
              <Input
                title="Código"
                placeholder="Código de checkin"
                value={dayCode}
                onChange={(e) => setDayCode(e.target.value)}
                required
                style={{ width: 250 }}
              />

              <ButtonLoading
                label={"Adicionar dia"}
                onClick={() => addDay()}
                className="landing-hover-lift"
                style={styles.addDayButton}
              />
            </Row>

            <TableContainer component={Paper} sx={styles.tableContainer}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={styles.headCell}>
                      Título
                    </TableCell>
                    <TableCell align="center" sx={styles.headCell}>
                      Data
                    </TableCell>
                    <TableCell align="center" sx={styles.headCell}>
                      Código
                    </TableCell>
                    <TableCell align="center" sx={styles.headCell}>
                      ID
                    </TableCell>
                    <TableCell align="center" sx={styles.headCell} width={100} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {days &&
                    days.map((day: any) => (
                      <TableRow key={day.id} sx={styles.bodyRow}>
                        <TableCell align="center" style={{ width: 400 }}>
                          <Row
                            style={{
                              alignItems: "center",
                              gap: 8,
                              justifyContent: "center",
                            }}
                          >
                            <Box
                              style={{
                                ...styles.statusDot,
                                backgroundColor: isFutureOrToday(day.date)
                                  ? colors.status.green
                                  : "#C7CFDB",
                              }}
                            />

                            {editingDay?.id === day.id ? (
                              <Input
                                value={newTitle}
                                onChange={(e) => setNewTitle(e.target.value)}
                                required
                                style={{ width: 400 }}
                              />
                            ) : (
                              <span style={styles.dayTitleText}>
                                {day.title}
                              </span>
                            )}
                          </Row>
                        </TableCell>
                        <TableCell align="center" style={{ width: 200 }}>
                          {editingDay?.id === day.id ? (
                            <Input
                              type="date"
                              value={newDate}
                              required
                              onChange={(e) => setNewDate(e.target.value)}
                              style={{ width: 200 }}
                            />
                          ) : (
                            <span style={styles.dayCellText}>
                              {formatDatePtBr(new Date(day.date))}
                            </span>
                          )}
                        </TableCell>
                        <TableCell align="center" style={{ width: 200 }}>
                          {editingDay?.id === day.id ? (
                            <Input
                              value={newCode}
                              onChange={(e) => setNewCode(e.target.value)}
                              required
                              style={{ width: 200 }}
                            />
                          ) : (
                            <span style={styles.dayCellText}>{day.code}</span>
                          )}
                        </TableCell>
                        <TableCell align="center" style={{ width: 200 }}>
                          <span style={styles.dayIdText}>{day.id}</span>
                        </TableCell>
                        <TableCell align="center" style={{ width: 100 }}>
                          {editingDay?.id === day.id ? (
                            <IconButton onClick={handleUpdate} size="small">
                              <CheckOutlinedIcon
                                sx={{ color: colors.status.green }}
                              />
                            </IconButton>
                          ) : (
                            <Row
                              style={{
                                gap: 4,
                                justifyContent: "center",
                              }}
                            >
                              <IconButton
                                onClick={() => handleEditClick(day)}
                                size="small"
                              >
                                <EditOutlinedIcon
                                  sx={{ fontSize: 18, color: colors.primary }}
                                />
                              </IconButton>
                              <IconButton
                                onClick={() => handleDeleteClick(day)}
                                size="small"
                              >
                                <DeleteOutlineIcon
                                  sx={{ fontSize: 18, color: colors.status.red }}
                                />
                              </IconButton>
                            </Row>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <Box style={styles.actionsBar}>
        <Row>
          {activeStep > 0 && (
            <ButtonLoading
              isDelete
              style={{ color: colors.primary, borderColor: colors.primary }}
              label="Voltar"
              icon={<ArrowBackIcon sx={{ fontSize: 18, marginRight: "6px" }} />}
              onClick={() => setActiveStep(activeStep - 1)}
            />
          )}
        </Row>
        <ButtonLoading label="Cancelar" isDelete onClick={close} />
        {activeStep < steps.length - 1 && (
          <ButtonLoading
            label="Próximo"
            className="landing-hover-lift"
            endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
            onClick={goToNextStep}
          />
        )}
        {activeStep === steps.length - 1 && (
          <ButtonLoading
            label="Salvar evento"
            className="landing-hover-lift"
            onClick={saveEvent}
            style={{ width: 200 }}
          />
        )}
      </Box>

      <DialogConfirm
        open={openConfirmDeleteDay}
        setOpen={setOpenConfirmDeleteDay}
        entity={`o dia "${dayToDelete?.title ?? ""}"`}
        onResponse={handleConfirmDeleteDay}
      />
    </Dialog>
  );
}

const styles: any = {
  dialogPaper: {
    backgroundColor: colors.white,
    borderRadius: "20px",
    maxHeight: "88vh",
  },

  dialogTitle: {
    fontSize: "22px",
    fontWeight: 700,
    color: "#14213D",
    paddingTop: "32px",
    paddingLeft: "48px",
  },

  stepperWrap: {
    width: "100%",
    padding: "24px 48px 0px",
  },

  dialogContent: {
    paddingLeft: "48px",
    paddingRight: "48px",
    paddingTop: "24px",
  },

  title: {
    fontSize: 18,
    fontWeight: 700,
    color: "#14213D",
    marginTop: "8px",
    marginBottom: "8px",
  },

  hintText: {
    fontSize: 13,
    color: "#5B6472",
    display: "block",
    marginTop: 4,
    marginBottom: 16,
  },

  activeRow: {
    alignItems: "center",
    gap: 12,
    marginTop: 24,
    width: "fit-content",
  },

  activeLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: "#14213D",
  },

  sectionDivider: {
    height: 1,
    backgroundColor: "#E5E9F0",
    width: "100%",
    marginTop: 28,
    marginBottom: 24,
  },

  uploadCard: {
    position: "relative",
    width: "100%",
    maxWidth: 640,
    aspectRatio: "760 / 425",
    borderRadius: 16,
    border: "1.5px dashed #C7D2E0",
    backgroundColor: "#F7F9FC",
    overflow: "hidden",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  uploadCardSmall: {
    position: "relative",
    width: "100%",
    minHeight: 140,
    borderRadius: 14,
    border: "1.5px dashed #C7D2E0",
    backgroundColor: "#F7F9FC",
    overflow: "hidden",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  uploadEmptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: 24,
    textAlign: "center",
  },

  uploadEmptyTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#5B6472",
  },

  uploadEmptyHint: {
    fontSize: 12,
    color: "#9AA5B5",
  },

  uploadPreviewImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  uploadOverlay: {
    position: "absolute",
    bottom: 12,
    right: 12,
    display: "flex",
    alignItems: "center",
    gap: 6,
    backgroundColor: "rgba(20,33,61,0.75)",
    color: colors.white,
    fontSize: 12,
    fontWeight: 600,
    padding: "6px 12px",
    borderRadius: 100,
  },

  canvaRow: {
    marginTop: 12,
    alignItems: "center",
    gap: 10,
  },

  canvaLink: {
    fontSize: 14,
    fontWeight: 600,
  },

  warningBox: {
    marginTop: 12,
    padding: "12px 16px",
    backgroundColor: "#FFF8EA",
    border: "1px solid #F5E1A8",
    borderRadius: 12,
    maxWidth: 640,
  },

  warningText: {
    fontSize: 13,
    color: "#8A6A1B",
    lineHeight: 1.5,
  },

  certificateStepRow: {
    alignItems: "flex-start",
    gap: 32,
    marginTop: 24,
    flexWrap: "wrap",
  },

  certificateFormCol: {
    width: "40%",
    minWidth: 320,
    display: "flex",
    flexDirection: "column",
  },

  colorFieldWrap: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 10,
  },

  colorSwatch: {
    width: 40,
    height: 40,
    borderRadius: 10,
    border: "1.5px solid #E5E9F0",
    marginBottom: 2,
    flexShrink: 0,
  },

  certificatePreviewWrap: {
    flex: 1,
    minWidth: 320,
    borderRadius: 16,
    overflow: "hidden",
    border: "1px solid #E5E9F0",
    boxShadow: "0 10px 24px -14px rgba(20,33,61,.20)",
  },

  certificate: {
    width: "100%",
    height: "420px",
    margin: 0,
    padding: 0,
    backgroundPosition: "center",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    backgroundColor: "#F7F9FC",
  },

  addDayButton: {
    width: 200,
    height: 55,
    marginTop: 20,
    borderRadius: 12,
  },

  tableContainer: {
    marginTop: "32px",
    borderRadius: "16px",
    border: "1.5px solid #E5E9F0",
    boxShadow: "0 10px 24px -16px rgba(20,33,61,.16)",
  },

  headCell: {
    backgroundColor: "#F7F9FC",
    color: "#5B6472",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    borderBottom: "1.5px solid #E5E9F0",
  },

  bodyRow: {
    "&:hover": {
      backgroundColor: "#F7F9FC",
    },
    "&:last-child td": {
      borderBottom: 0,
    },
  },

  statusDot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
    flexShrink: 0,
  },

  dayTitleText: {
    fontSize: 14,
    fontWeight: 600,
    color: "#14213D",
  },

  dayCellText: {
    fontSize: 14,
    color: "#5B6472",
  },

  dayIdText: {
    fontSize: 12,
    color: "#9AA5B5",
  },

  actionsBar: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    padding: "20px 48px",
    borderTop: "1px solid #E5E9F0",
  },
};
